import type { TFunction } from "i18next";

const SUPPORTED_EXTENSIONS = [".pdf", ".docx"];

export const ACCEPTED_FILE_TYPES = SUPPORTED_EXTENSIONS;

const extensionOf = (fileName: string) => {
  const dot = fileName.lastIndexOf(".");
  return dot < 0 ? "" : fileName.slice(dot).toLowerCase();
};

export const fileTypeProblem = (fileName: string, t: TFunction): string | null => {
  const extension = extensionOf(fileName);
  if (SUPPORTED_EXTENSIONS.includes(extension)) return null;
  const supported = t("We support PDF and Word (.docx).");
  return extension === ".doc" ? `${supported} ${t("Save the document as .docx and try again.")}` : supported;
};

const pageRanges = (pages: number[]): string[] => {
  const sorted = [...new Set(pages)].sort((a, b) => a - b);
  const ranges: string[] = [];
  let start = sorted[0];
  let previous = sorted[0];
  for (const page of sorted.slice(1)) {
    if (page === previous + 1) {
      previous = page;
      continue;
    }
    ranges.push(start === previous ? `${start}` : `${start}–${previous}`);
    start = page;
    previous = page;
  }
  if (start !== undefined) ranges.push(start === previous ? `${start}` : `${start}–${previous}`);
  return ranges;
};

export const unreadablePagesNotice = (pages: number[], t: TFunction): string | null => {
  const ranges = pageRanges(pages);
  if (ranges.length === 0) return null;
  const listed =
    ranges.length === 1 ? ranges[0] : `${ranges.slice(0, -1).join(", ")} ${t("and")} ${ranges[ranges.length - 1]}`;
  return t("Page {{pages}} could not be read (it appears to be scanned).", { pages: listed });
};

export interface ContractAnalysis {
  title: string;
  description: string;
  templateId: string | null;
  startDate: string | null;
  endDate: string | null;
  paymentTerms: number | null;
  noticePeriod: number | null;
  counterparty: { companyName: string; fullName?: string; email?: string };
}

export const parseAnalysis = (content: string | null | undefined): ContractAnalysis => {
  if (!content) throw Error("Could not analyze document");
  return JSON.parse(content.replace(/```json\s*|\s*```/g, "")) as ContractAnalysis;
};

export const saveThenReload = async (save: () => Promise<unknown>, reload: () => Promise<unknown>): Promise<void> => {
  await save();
  await reload().catch(() => undefined);
};
